import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type {
  GameStatus,
  PlayerStatusAction,
  GameStage,
  PlayerAction,
  HandName,
} from '../constants/game';
import type { CardContent } from '../components/Card/FrontEnd';
import { formatNumberInFixedDecimal } from '../utils/number';
export type Address = `0x${string}`;
interface GameConfig {
  bigBlind: number;
  smallBlind: number;
  maxInitialBuyIn: number;
  allowRunTwice: boolean;
  useCentsValue: boolean;
  allowMatchStack: boolean;
  allowRabbitHunting: boolean;
  allowUtgStraddle2bb: boolean;
  decisionTimeLimit: number;
  currency: string; // TODO: read from contract
  collateralToken: string;
}

export interface GameInfo {
  txnHash?: Address;
  network?: string;
  status?: GameStatus;
  config?: GameConfig;
  isPaused?: boolean;
  isRoundReady?: boolean;
  adminAddress?: Address;
  adminNickname?: string | null;
  lastActionTime?: number; // the start time of the current player;
  pausedAt?: number; // the paused time of the game
  roundId?: number; // current round/hand number
}

export type StagePot = Record<GameStage, number>;

export type CurrentStagePot = {
  p: number;
  b: number;
  minRaise: number;
};

export type Pots = {
  stages: StagePot;
  currentStage: CurrentStagePot;
};

export type SitUser = {
  position: string;
  nickname: string;
  isAway: boolean;
  balance: number;
  predictedBalance: number;
};
export interface GameInfoAdditionalResp {
  foldedUsers: string[];
  allInUsers: string[];
  allowedActions: PlayerAction[];
  pots: Pots;
  sitUsers: Record<Address, SitUser>; // sitted users without enough balance to take actions
  userToAct: string | null;
  currentStage: GameStage;
  actionStartTime: number; // the start time of the current player;
}

export interface User {
  userAddress: Address;
  balance: number;
  position: string;
  nickname?: string;
  predictedBalance: number;
}

export interface UserInfo {
  users: Record<Address, User>;
  idle_users: Record<Address, User>;
  dealer: Address;
  smallBlind: Address;
  bigBlind: Address;
  utg: Address;
}

export interface AddtionalUserInfo {
  currentStageRaise: number;
  lastStageBetAmount: number;
  winAmount: number;
  betAmount: number;
  balanceChange: number;
  isAway: boolean;
  isFolded: boolean;
  isAllIn: boolean;
  isActed: boolean;
  cards?: CardContent[];
  status?: PlayerStatusAction;
}

export interface OtherCard {
  cards: CardContent[];
  handName: HandName;
}
export interface CardInfo {
  holeCards: CardContent[];
  communityCards: CardContent[];
  otherCards: Record<Address, OtherCard>;
  handName: HandName | null;
  rabbitHuntingCards?: CardContent[] | null;
}

export interface GameState {
  gameInfo?: GameInfo;
  systemTime?: number; // timestamp in seconds
  addtionalResp?: GameInfoAdditionalResp;
  userInfo?: UserInfo;
  addtionalUserInfos?: Record<Address, AddtionalUserInfo>;
  cardInfo?: CardInfo;
}

const DIVIDE_HUNDRED_PROPERTIES = ['smallBlind', 'bigBlind', 'maxInitialBuyIn'];

const initialState: GameState = {};

export const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    getGameInfo: (state, action: PayloadAction<GameState>) => {
      if (action.payload.systemTime! > (state.systemTime ?? 0)) {
        const newState = action.payload;
        for (const property of DIVIDE_HUNDRED_PROPERTIES) {
          // @ts-ignore
          if (newState.gameInfo?.config?.[property]) {
            // @ts-ignore
            newState.gameInfo.config[property] = formatNumberInFixedDecimal(
              // @ts-ignore
              newState.gameInfo.config[property] / 100,
              2
            );
          }
        }
        return newState;
      }
    },
  },
});

export const { getGameInfo } = gameSlice.actions;

export default gameSlice.reducer;
